import { createElement, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';

interface TurnstileApi {
  render(container: HTMLElement, options: {
    sitekey: string;
    callback(token: string): void;
    'expired-callback'(): void;
    'error-callback'?(): void;
  }): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

function api(): TurnstileApi | undefined {
  return (window as unknown as { turnstile?: TurnstileApi }).turnstile;
}

export const LOAD_ERROR =
  'Could not load the verification widget. Disable any blocker for challenges.cloudflare.com and reload.';

let loading: Promise<void> | null = null;

/** Adds the Turnstile script to the page the first time it is asked for. */
export function loadTurnstile(): Promise<void> {
  if (loading) return loading;
  loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      loading = null;
      reject(new Error('Could not load the verification widget.'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

/** Test hook: forget that the script was requested. */
export function resetTurnstileForTests(): void {
  loading = null;
}

/**
 * Renders a Turnstile widget when `siteKey` is set. Each token, and each expiry
 * (as null), is reported through `onToken`.
 */
export function useTurnstile(
  siteKey: string | null,
  onToken: (token: string | null) => void,
): { element: ReactElement | null; reset(): void; loadError: string | null } {
  const [loadError, setLoadError] = useState<string | null>(null);
  const container = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  useEffect(() => {
    if (siteKey === null) return;
    let cancelled = false;
    const mount = (): void => {
      const turnstile = api();
      if (cancelled || !turnstile || !container.current) return;
      widgetId.current = turnstile.render(container.current, {
        sitekey: siteKey,
        callback: (token) => onTokenRef.current(token),
        'expired-callback': () => onTokenRef.current(null),
        'error-callback': () => onTokenRef.current(null),
      });
    };
    // The script is requested once; if its API is already present, mount now.
    const loaded = loadTurnstile();
    loaded.catch(() => {
      if (!cancelled) setLoadError(LOAD_ERROR);
    });
    if (api()) mount();
    else void loaded.then(mount).catch(() => undefined);
    return () => {
      cancelled = true;
      setLoadError(null);
      if (widgetId.current !== null) api()?.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [siteKey]);

  const reset = useCallback(() => {
    if (widgetId.current !== null) api()?.reset(widgetId.current);
  }, []);

  const element = siteKey === null ? null : createElement('div', { ref: container, className: 'min-h-turnstile' });
  return { element, reset, loadError };
}
